import { endOfDay } from 'date-fns';
import { useState, useMemo, useCallback } from 'react';
import _cloneDeep from 'lodash/cloneDeep';
import _forEach from 'lodash/forEach';
import _isEqual from 'lodash/isEqual';

import { get, isEmpty } from 'ravenjs/utils/lodash';
import { addSchemaValue } from 'ravenjs/utils/schema';
import { formatDate } from 'ravenjs/utils/date';

import { PREPPED_ANSWER_UPDATES } from 'constants/tickets';
import MESSAGES from 'constants/messages';
import { MODALS } from 'constants/modals';
import { CaseCaveFieldLocalStorage } from 'utils/caseCaveFieldLocalStorage';
import { asyncDebounce } from 'utils/debounce';

export function useSuggestedUpdates({
    cellTypes,
    currentUser,
    currentUserId,
    fetchAllSuggestedPAUpdates,
    fetchUsers,
    isLoading,
    openModal,
    schema,
    userRoleIds,
}) {
    const localStorage = useMemo(() => {
        return new CaseCaveFieldLocalStorage(
            currentUserId?.toString(),
            PREPPED_ANSWER_UPDATES.SUGGEST_UPDATE.LOCAL_STORAGE_KEYS.ID,
            PREPPED_ANSWER_UPDATES.SUGGEST_UPDATE.LOCAL_STORAGE_KEYS.FIELD_KEY
        );
    }, [currentUserId]);

    const defaultFilters = useMemo(() => {
        const defaultFiltersStored = localStorage.get() || {
            'filter[current_state]': 'open',
            'filter[with_expired][eq]': true,
        };

        _forEach(defaultFiltersStored, (value, key) => {
            if (key === 'filter[created_at][gte]') {
                defaultFiltersStored[key] = new Date(value);
            } else if (key === 'filter[created_at][lte]') {
                defaultFiltersStored[key] = endOfDay(new Date(value));
            }
        });

        if (
            !defaultFiltersStored['filter[prepped_answer_author_id]'] &&
            !isEmpty(currentUser.info)
        ) {
            const firstName = get(currentUser, 'info.firstName', '');
            const lastName = get(currentUser, 'info.lastName', '');

            defaultFiltersStored['filter[prepped_answer_author_id]'] = [
                {
                    label: `${firstName} ${lastName || ''}`,
                    value: currentUserId.toString(),
                },
            ];
        }

        return defaultFiltersStored;
    }, [currentUser, currentUserId, localStorage]);

    const [error, setError] = useState('');
    const [params, setParams] = useState({
        'page[number]': 1,
        'page[size]': 10,
        'stats[total]': 'count',
        sort: '-created_at',
    });
    const [appliedFilters, setAppliedFilters] = useState({ ...defaultFilters });
    const [listData, setListData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);

    const listSchema = useMemo(() => {
        const listSchemaCopy = _cloneDeep(schema);
        const filters = get(listSchemaCopy, 'filters', []);

        addSchemaValue(listSchemaCopy, 'pagination.totalRows', Number(totalRecords));
        addSchemaValue(listSchemaCopy, 'list.cellTypes', cellTypes);
        addSchemaValue(listSchemaCopy, 'filters', [
            ...filters,
            {
                name: 'filter[with_expired][eq]',
                title: PREPPED_ANSWER_UPDATES.SUGGEST_UPDATE.CHECK_BOX,
                type: 'checkbox',
            },
        ]);

        return listSchemaCopy;
    }, [cellTypes, schema, totalRecords]);

    async function getAllPreppedAnswerUsers(searchSpec, callback) {
        try {
            const response = await fetchUsersList(searchSpec);
            const users = get(response, 'users', []);
            const paUpdateUser = users.map(({ firstName, lastName, userId }) => ({
                label: `${firstName} ${lastName || ''}`,
                value: userId.toString(),
            }));

            callback(paUpdateUser);

            return paUpdateUser;
        } catch (e) {
            return e;
        }
    }

    async function fetchUsersList(searchSpec = '') {
        return fetchUsers({
            limit: 10,
            roleId: userRoleIds,
            searchSpec,
            sort: 'firstName',
        });
    }

    const fetchData = useCallback(
        async (currentAppliedFilters, currentParams, isPageChange = false) => {
            const paAuthorIds = get(currentAppliedFilters, 'filter[prepped_answer_author_id]', []);
            const requesterIds = get(currentAppliedFilters, 'filter[requester_id]', []);

            let preppedAnswerExpiredFilterParam = {};

            let isExpiredFilterExist = false;
            const mergedAppliedFilters = {
                ...currentAppliedFilters,
                ...currentParams,
            };
            Object.entries(mergedAppliedFilters).forEach(function([key]) {
                if (key === 'filter[with_expired][eq]') {
                    isExpiredFilterExist = true;
                }
            });

            if (!isExpiredFilterExist) {
                preppedAnswerExpiredFilterParam = {
                    'filter[with_expired][eq]': false,
                };
            }

            try {
                isLoading(true);
                const suggestedPAEditsResponse = await fetchAllSuggestedPAUpdates({
                    ...currentParams,
                    ...currentAppliedFilters,
                    ...preppedAnswerExpiredFilterParam,
                    'filter[prepped_answer_author_id]': paAuthorIds.map(filterOption =>
                        filterOption.value.toString()
                    ),
                    'filter[requester_id]': requesterIds.map(filterOption =>
                        filterOption.value.toString()
                    ),
                });
                const suggestedPAEditsData = get(suggestedPAEditsResponse, 'data', []);

                const totalRecords = get(suggestedPAEditsResponse, 'meta.stats.total.count', 0);

                const userIdSet = new Set();

                suggestedPAEditsData.forEach(({ attributes }) => {
                    userIdSet.add(attributes.prepped_answer_author_id);
                    userIdSet.add(attributes.requester_id);
                });

                const usersResponse = await fetchUsers({ userId: [...userIdSet] });
                const users = get(usersResponse, 'users', []);
                const usersMap = {};
                users.forEach(user => (usersMap[user.userId] = user.fullName));
                const listData = suggestedPAEditsData.map(({ attributes, id }) => {
                    return {
                        author: usersMap[attributes.prepped_answer_author_id],
                        id,
                        preppedAnswerId: attributes.prepped_answer_id,
                        requester: usersMap[attributes.requester_id],
                        title: get(attributes, 'requested_changes.old_title'),
                        dateSuggested: formatDate(attributes.created_at),
                    };
                });

                setListData(listData);
                if (!isPageChange) {
                    setTotalRecords(totalRecords);
                }
            } catch (error) {
                setError(error);
            } finally {
                isLoading(false);
            }
        },
        [fetchAllSuggestedPAUpdates, fetchUsers, isLoading]
    );

    const fetchInitialData = useCallback(() => {
        fetchData(appliedFilters, params);
    }, [appliedFilters, fetchData, params]);

    const onChange = useCallback(
        options => {
            const { page } = options;

            const newParams = {
                ...params,
                'page[number]': page,
            };

            Object.keys(newParams).forEach(
                param => newParams[param] === '' && delete newParams[param]
            );

            if (!_isEqual(params, newParams)) {
                setParams(newParams);
                fetchData(appliedFilters, newParams);
            }
        },
        [appliedFilters, fetchData, params]
    );

    const onApplyFilter = useCallback(
        filters => {
            const nextAppliedFilters = { ...filters };

            const createdStartDate = get(nextAppliedFilters, 'filter[created_at][gte]', null);
            const createdEndDate = get(nextAppliedFilters, 'filter[created_at][lte]', null);

            if (createdEndDate) createdEndDate.setHours(23, 59, 59, 999);

            if (createdStartDate && createdEndDate && createdStartDate > createdEndDate) {
                openModal(MODALS.CONFIRM, {
                    title: MESSAGES.MODAL.CONFIRM.TITLE.ERROR,
                    description: MESSAGES.MODAL.DATE_VALIDATION_MESSAGE,
                });
            } else {
                setAppliedFilters(nextAppliedFilters);
                localStorage.set(nextAppliedFilters);
                fetchData(nextAppliedFilters, params);
            }
        },
        [fetchData, localStorage, openModal, params]
    );

    const getPAUpdateUserDebounced = asyncDebounce(getAllPreppedAnswerUsers, 500);

    const getActions = useCallback(() => {
        return {
            getPAUpdateUser: getPAUpdateUserDebounced,
            handleOnChange: onChange,
            onApplyFilter,
        };
    }, [getPAUpdateUserDebounced, onApplyFilter, onChange]);

    return {
        listData,
        listSchema,
        getActions,
        fetchInitialData,
        appliedFilters,
        error,
    };
}
